import { Children, useCallback, useEffect, useState } from 'react';

import './TabControl.scss';

/**A tab to display within the TabContainer.
 * @param {Object} props Incoming JSX attributes.
 * @param {boolean} props.disabled If true, this tab is not selectable.
 * @param {string} props.title String to display in the tab's table.
 */
function Tab(props) {
    return props.children;
}

/**A container for toggling between child Tab elements.
 * @param {Object} props Incoming JSX attributes.
 * @param {[Tab]} props.children Array of Tab components.
 */
function TabContainer(props) {
    const [currentlyVisible, setCurrentlyVisible] = useState(typeof props?.defaultTab === 'number' ? props.defaultTab : 0);
    const tabs = Children.toArray(props.children);

    Children.map(tabs, (child) => {
        if (child.type.name !== Tab.name) {
            throw Error(`"${child.type.name}" is not a valid child of TabContainer.`);
        }
    });

    const tryFlipTab = (tabIndex) => {
        if (Children.toArray(props.children)[tabIndex].props.disabled) {
            return;
        }
        setCurrentlyVisible(tabIndex);
    };

    const classesFor = (tab, i) => {
        let classes = [];
        classes.push('tab-control-selector');
        if (currentlyVisible === i) classes.push('selected-tab');
        if (tab.props.disabled) classes.push('disabled');
        return classes.join(' ');
    };

    return (
        <div className="tab-control">
            <div
                className="tab-control-selector-row"
                role="tablist"
            >
                {tabs.map((tab, i) => {
                    return (
                        <div
                            key={'tab-key-' + i}
                            className={classesFor(tab, i)}
                            onClick={() => {
                                tryFlipTab(i);
                            }}
                            aria-selected={i === currentlyVisible}
                            role="tab"
                        >
                            {tab.props.title ? tab.props.title : ''}
                        </div>
                    );
                })}
            </div>
            <div
                className="tab-control-content"
                role="tabpanel"
            >
                {tabs[currentlyVisible]}
            </div>
        </div>
    );
}

export default function TabControl(props) {
    const [tabs, setTabs] = useState(props.children);
    const [currentlyVisible, setCurrentlyVisible] = useState(typeof props?.defaultTab === 'number' ? props.defaultTab : 0);
    const deriveSelectors = useCallback(() => {
        return props.selectors.map((tab, i) => {
            return (
                <div
                    key={'key-' + tab}
                    className={(currentlyVisible === i ? 'selected-tab ' : '') + 'tab-control-selector'}
                    onClick={() => {
                        setCurrentlyVisible(i);
                    }}
                >
                    {tab}
                </div>
            );
        });
    }, [props.selectors, currentlyVisible]);
    const [selectors, setSelectors] = useState(() => deriveSelectors());

    useEffect(() => {
        setSelectors(() => deriveSelectors());
    }, [currentlyVisible, deriveSelectors]);

    useEffect(() => {
        setTabs(props.children);
    }, [props.children]);

    return (
        <div
            className={
                typeof props?.classes === 'object' && props.classes.length > 0
                    ? ['tab-control'].concat(props.classes).join(' ')
                    : 'tab-control'
            }
        >
            <div className="tab-control-selector-row">{selectors}</div>
            <div className="tab-control-content">{tabs[currentlyVisible]}</div>
        </div>
    );
}

export { Tab, TabContainer };
